import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * BW10 — Documents (Wallet) data hooks. The `document` table is shaped so
 * sensitive fields (numbers) flow through a pgcrypto RPC pair instead of
 * being read directly from the row.
 */

export type DocumentRow = {
  id: string;
  user_id: string;
  category: string;
  kind: string;
  title: string;
  issuer: string | null;
  issued_at: string | null;
  expires_at: string | null;
  issued_country: string | null;
  related_project_id: string | null;
  metadata: Record<string, unknown>;
  image_url: string | null;
  nfc_payload: Record<string, unknown> | null;
  notes: string;
  wallet_pass_url: string | null;
  wallet_pass_generated_at: string | null;
  wallet_pass_signed: boolean;
  created_at: string;
  updated_at: string;
};

const SELECT =
  "id, user_id, category, kind, title, issuer, issued_at, expires_at, issued_country, related_project_id, metadata, image_url, nfc_payload, notes, wallet_pass_url, wallet_pass_generated_at, wallet_pass_signed, created_at, updated_at";

function dbAny() {
  return supabase as unknown as { from: (t: string) => any };
}

export function useDocuments() {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["documents", userId],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async (): Promise<DocumentRow[]> => {
      if (!userId) return [];
      const { data, error } = await dbAny()
        .from("document")
        .select(SELECT)
        .eq("user_id", userId)
        .order("category", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as DocumentRow[];
    },
  });
}

export function useDocument(id: string | undefined) {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["document", id, userId],
    enabled: Boolean(id && userId),
    staleTime: 0,
    queryFn: async (): Promise<DocumentRow | null> => {
      if (!id) return null;
      const { data, error } = await dbAny()
        .from("document")
        .select(SELECT)
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as DocumentRow | null;
    },
  });
}

/**
 * Fetch the decrypted document number on demand. Read separately from the
 * row so the UI can require an explicit "reveal" tap.
 */
export async function fetchDocumentNumber(id: string): Promise<string | null> {
  const { data, error } = await supabase.rpc("document_get_number" as never, {
    p_id: id,
  } as never) as unknown as { data: string | null; error: Error | null };
  if (error) throw error;
  return data ?? null;
}

export async function setDocumentNumber(id: string, plaintext: string | null): Promise<void> {
  const { error } = await supabase.rpc("document_set_number" as never, {
    p_id: id,
    p_number: plaintext,
  } as never) as unknown as { error: Error | null };
  if (error) throw error;
}

export const DOCUMENT_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: "identity", label: "Identity" },
  { value: "immigration", label: "Immigration" },
  { value: "travel", label: "Travel" },
  { value: "ticket", label: "Tickets" },
  { value: "employment", label: "Employment" },
  { value: "certification", label: "Certifications" },
  { value: "membership", label: "Memberships" },
  { value: "vehicle", label: "Vehicles" },
  { value: "property", label: "Housing" },
  { value: "financial", label: "Financial" },
  { value: "medical", label: "Health" },
  { value: "other", label: "Other" },
];

export function categoryLabel(category: string): string {
  return DOCUMENT_CATEGORIES.find((c) => c.value === category)?.label ?? category;
}

export function daysUntilExpiry(expires: string | null): number | null {
  if (!expires) return null;
  const target = new Date(`${expires}T00:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 86_400_000);
}
